import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useContext, useRef, useEffect, Suspense } from "react";
import UserContext from "common/Context/UserContext";
import Home from "pages/Home";
import Dashboard from "pages/Dashboard";
import PageNotFound from "common/Components/PageNotFound";
import Admin from "pages/Admin/Admin";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import Groups from "pages/Admin/Groups";
import CreateCustomer from "pages/Admin/Groups/CreateCustomer";
import ThingType from "pages/Admin/ThingType";
import CreateUser from "pages/Admin/CreateUser";
import AccessDenied from "common/Components/AccessDenied";
import { ConfirmResetPassword } from "pages/Authentication";
import Administration from "pages/Home/Administration";
import BaseComponent from "common/Components/BaseComponent";
import CustomerAccessRevoked from "common/Components/CustomerAccessRevoked";
import { CloudView } from "common/Constants/route-components";
import PlatformUserAccess from "pages/PlatformUserAccess";
import { isPlatformAdminProfile } from "common/Utilities/Role.utils";
import { isPlatformAdminHomePath } from "common/Utilities/CustomerSelection.utils";
import EffectiveAccessRouteGuard from "./EffectiveAccessRouteGuard";
import {
  redirectToLiveApplication,
  shouldNoneUserUseAdministrationLanding,
} from "common/Utilities/EffectiveAccess.utils";

const errorMessage =
  "Access restricted: You do not have permission to view this page.";

const ADMIN_ROUTE_ACCESS_KEYS = ["all", "admin", "datamanagement"];

const isPlatformAdminBasePath = (pathname = "") =>
  isPlatformAdminHomePath(pathname);

const HomeRoutes = () => {
  const { userProfile } = useContext(UserContext);
  const { pathname } = useLocation();
  const profile = userProfile?.profile ?? {};
  const { accessTo: userAccessPages = [] } = profile;
  const hasRedirected = useRef(false);

  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.includes("undefined")) {
      const segments = currentPath
        .split("/")
        .filter((segment) => segment && segment !== "undefined");
      const cleanPath = segments.length ? `/${segments.join("/")}` : "/";
      window.history.replaceState(null, null, cleanPath);
    }
  }, []);

  const buildRoutePaths = (routes) =>
    routes.map((item) => ({
      [item.value]: item.route,
    }));

  const superAdminRoutes = buildRoutePaths(
    INFRAMARK_CONSTANTS.routes.superAdminRoutes
  );
  const userAdminRoutes = buildRoutePaths(
    INFRAMARK_CONSTANTS.routes.userAdminRoutes
  );
  const accessTo = userAccessPages.map((key) => String(key).toLowerCase());
  const usesMeAccess = Boolean(profile?.sessionFromMe && profile?.effectiveAccess);
  const hasSessionWarning = Boolean(profile?.sessionWarning);
  const hasAdministrationRouteAccess = accessTo.some((key) =>
    ADMIN_ROUTE_ACCESS_KEYS.includes(key)
  );
  const isPlatformAdmin = isPlatformAdminProfile(profile);
  const shouldShowRevokedAccessScreen =
    hasSessionWarning && !(isPlatformAdmin && isPlatformAdminBasePath(pathname));

  const baseRoutes = isPlatformAdmin ? superAdminRoutes : userAdminRoutes;
  const pageRoutes = [
    ...baseRoutes,
    { Home: "/" },
    { AccessDenied: "/access_denied" },
  ];

  const routeMatchesAccess = (routeKeys) =>
    routeKeys.some((key) => accessTo.includes(key.toLocaleLowerCase()));

  const accessibleRoutes = accessTo.includes("all")
    ? pageRoutes
    : pageRoutes.filter((obj) => routeMatchesAccess(Object.keys(obj)));

  const isAccessable = (page) =>
    isPlatformAdmin || accessibleRoutes.some((obj) => page in obj);

  const canAccessAdministration =
    isAccessable("DataManagement") ||
    isAccessable("Admin") ||
    isAccessable("Dashboard") ||
    (usesMeAccess && hasAdministrationRouteAccess);

  if (shouldShowRevokedAccessScreen) {
    return (
      <Routes>
        <Route
          path="*"
          element={
            <CustomerAccessRevoked
              message={profile.sessionWarning}
              title={profile.sessionWarningTitle}
            />
          }
        />
      </Routes>
    );
  }

  const renderRootRouteElement = () => {
    if (accessibleRoutes.length === 1) {
      const onlyRoute = accessibleRoutes[0];
      const onlyKey = Object.keys(onlyRoute)[0];

      if (
        usesMeAccess &&
        hasAdministrationRouteAccess &&
        onlyKey === "Dashboard"
      ) {
        const adminPath = INFRAMARK_CONSTANTS?.navigationRoutes?.administration;
        if (adminPath && !hasRedirected.current) {
          hasRedirected.current = true;
          return <Navigate to={adminPath} replace />;
        }
      }

      if (onlyKey === "Dashboard") {
        if (!hasRedirected.current) {
          hasRedirected.current = true;
          redirectToLiveApplication();
        }
        return null;
      }

      const firstRoute = Object.values(onlyRoute)[0];
      if (
        firstRoute &&
        typeof firstRoute === "string" &&
        !firstRoute.includes("undefined")
      ) {
        if (!hasRedirected.current) {
          hasRedirected.current = true;
          return <Navigate to={firstRoute} replace />;
        }
      }
    }

    if (accessibleRoutes.length > 0) {
      if (shouldNoneUserUseAdministrationLanding(profile)) {
        const adminPath = INFRAMARK_CONSTANTS?.navigationRoutes?.administration;
        if (adminPath) {
          return <Navigate to={adminPath} replace />;
        }
      }
      return <Home />;
    }

    return <PageNotFound statusCode="3" errorText={errorMessage} showLogout />;
  };

  const renderHomeRouteElement = () => {
    if (accessibleRoutes.length === 0) {
      return <PageNotFound statusCode="3" errorText={errorMessage} />;
    }
    if (shouldNoneUserUseAdministrationLanding(profile)) {
      const adminPath = INFRAMARK_CONSTANTS?.navigationRoutes?.administration;
      if (adminPath) {
        return <Navigate to={adminPath} replace />;
      }
    }
    return <Home />;
  };

  return (
    <EffectiveAccessRouteGuard>
    <Routes>
      <Route path="/" element={renderRootRouteElement()} />
      <Route path="/home" element={renderHomeRouteElement()} />
      <Route
        path="/admin"
        element={
          isAccessable("Admin") ? (
            <Admin />
          ) : (
            <PageNotFound statusCode="3" errorText={errorMessage} />
          )
        }
      />
      <Route
        path="/dashboard"
        element={
          isAccessable("Dashboard") ? (
            <Dashboard />
          ) : (
            <PageNotFound statusCode="3" errorText={errorMessage} />
          )
        }
      />
      <Route
        path="/customers"
        element={
          isAccessable("Groups") ? (
            <Groups />
          ) : (
            <PageNotFound statusCode="3" errorText={errorMessage} />
          )
        }
      />
      <Route
        path="/createcustomer"
        element={
          isAccessable("Groups") ? (
            <CreateCustomer />
          ) : (
            <PageNotFound statusCode="3" errorText={errorMessage} />
          )
        }
      />
      <Route
        path="/thingtype"
        element={
          isAccessable("ThingType") ? (
            <ThingType />
          ) : (
            <PageNotFound statusCode="3" errorText={errorMessage} />
          )
        }
      />
      <Route
        path="/user-access"
        element={
          isAccessable("PlatformUserAccess") ? (
            <PlatformUserAccess />
          ) : (
            <PageNotFound statusCode="3" errorText={errorMessage} />
          )
        }
      />
      <Route
        path={INFRAMARK_CONSTANTS?.navigationRoutes?.administration}
        element={
          canAccessAdministration ? (
            <Administration />
          ) : (
            <PageNotFound statusCode="3" errorText={errorMessage} />
          )
        }
      />
      <Route
        path={INFRAMARK_CONSTANTS?.navigationRoutes?.cloudHierarchy}
        element={
          isAccessable("Dashboard") ? (
            <BaseComponent>
              <Suspense fallback={<div>Loading...</div>}>
                <CloudView />
              </Suspense>
            </BaseComponent>
          ) : (
            <PageNotFound statusCode="3" errorText={errorMessage} />
          )
        }
      />
      <Route
        path="/createuser"
        element={
          isAccessable("CreateUser") ? (
            <CreateUser />
          ) : (
            <PageNotFound statusCode="3" errorText={errorMessage} />
          )
        }
      />
      <Route path="/access_denied" element={<AccessDenied />} />
      <Route path="/confirm-reset-password" element={<ConfirmResetPassword />} />
      <Route path="/signup/invite" element={<Navigate to="/home" replace />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </EffectiveAccessRouteGuard>
  );
};

export default HomeRoutes;

/**
Conditions for logged in users:

 1) If user has multiple page permissions we will take him to the home page
 2) If user has single page permission we will directly take him to the permitted page

 { label: "ASSETS", value: "Assets", route: "/assets" },
    { label: "ADMIN", value: "Admin", route: "/admin" },
    { label: "DASHBOARD", value: "Dashboard", route: "/dashboard" },
    { label: "REPORTS", value: "Reports", route: "/reports" },
    { label: "ALL", value: "All", route: "/home" }
 */
